<template>
  <div class="c-population-detail-tabs">
    <BittsSegment
      :tabs="tabs"
      variant="segmented_control"
      :active-index="activeSegmentIndex"
      class="mb-16"
      @change:segment="handleChangeSegment"
    />
    <div
      v-if="activeSegmentIndex === 0"
      role="tabpanel"
      aria-label="Details"
      class="flex flex-col flex-1 self-stretch gap-16"
    >
      <PopulationFormFields
        v-if="activeSegmentIndex === 0"
        v-model:population-name="populationName"
        v-model:population-description="populationDescription"
        v-model:selected-feed-id="selectedFeedId"
        v-model:selected-object="selectedObject"
        v-model:selected-pop-type="selectedPopType"
        :form-config="formConfig"
        :data-source-config="dataSourceConfig"
        :original-population-name="originalPopulationName"
        :selected-feed="selectedFeed"
        @feed-change="handleFeedChange"
      />
      <div
        v-if="mode !== 'create' && population?.meta?.updated_at"
        class="flex items-center gap-4"
      >
        <span
          class="text-sm font-bold"
          :class="
            isViewMode
              ? 'text-neutral-text-placeholder opacity-100!'
              : 'text-neutral-text-strong'
          "
          >Last Updated:</span
        >
        <span class="text-neutral-text-weak text-sm">
          {{
            DateTime.fromISO(population?.meta?.updated_at).toFormat(
              'LLLL d, yyyy',
            )
          }}
        </span>
      </div>
    </div>
    <div
      v-if="activeSegmentIndex === 1"
      role="tabpanel"
      aria-label="Filters"
      class="flex flex-col gap-16"
    >
      <BittsAlert
        v-if="alertConfig.populationFiltersAlertVisibility && !isViewMode"
        color="info"
        :message="alertConfig.populationFiltersAlertContent?.message"
        :description="alertConfig.populationFiltersAlertContent?.description"
      />
      <PopulationFiltersAdvanced
        v-model="advancedFilterGroups"
        :field-options="advancedFilterFieldOptions"
        :disabled="props.mode === 'view'"
      />
    </div>
    <div
      v-if="showFooterButton"
      class="c-population-detail-tabs__buttons-container"
    >
      <BittsButton
        v-if="activeSegmentIndex === 0"
        text="Continue"
        :disabled="!isDetailsFormValid"
        @click="handleChangeSegment(1)"
        type="primary"
      />
      <BittsButton
        v-if="activeSegmentIndex === 1"
        text="Apply filters"
        :disabled="isApplyDisabled"
        @click="emit('apply-filters')"
        variant="outline"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsAlert, BittsButton, BittsSegment } from '@crossbeam/bitts';

import { DateTime } from 'luxon';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import {
  type FilterGroupOutput,
  isFilterComplete,
} from '@/components/filters/types';

import type { Feed } from '@/types/feeds';
import {
  type InternalFilterGroup,
  type OtherPopulationType,
  type Population,
  type PopulationAlertConfig,
  type PopulationDataSourceConfig,
  type PopulationFilterConfig,
  type PopulationFormConfig,
  type PopulationStats,
  type StandardPopulationType,
} from '@/types/populations';

import { sourcesToFieldOptions } from './populationFilterConverters';
import PopulationFiltersAdvanced from './PopulationFiltersAdvanced.vue';
import PopulationFormFields from './PopulationFormFields.vue';

export type PopulationMode = 'view' | 'edit' | 'create';

type Props = {
  mode: PopulationMode;
  isCustomPopulation: boolean;
  isOfflinePartner: boolean;
  isOpenData?: boolean;
  originalPopulationName?: string;
  selectedFeed: Feed | null;
  isSourceLocked?: boolean;
  population?:
    | (Population & {
        meta?: PopulationStats;
        isEmpty?: boolean;
        tag?: string;
      })
    | null;
  filterConfig: PopulationFilterConfig;
  alertConfig: PopulationAlertConfig;
  dataSourceConfig: PopulationDataSourceConfig;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  'feed-change': [feedId: number | 'add-new-source'];
  continue: [];
  'apply-filters': [];
  'add-filter-group': [value: boolean];
  'delete-filter-group': [value: InternalFilterGroup];
  'change-filter-group-list': [value: InternalFilterGroup[]];
}>();

const advancedFilterFieldOptions = computed(() => {
  const sources = props.filterConfig.completeFilterSources ?? [];
  const offlinePartnerUuid = props.filterConfig.offlinePartnerUuid;

  // For offline partners, use file_upload as the data source type
  // Otherwise use the feed's integration type
  const dataSourceType = offlinePartnerUuid
    ? 'file_upload'
    : props.selectedFeed?.integration?.type?.toLowerCase();

  const context = dataSourceType
    ? { dataSourceType, offlinePartnerUuid }
    : undefined;

  return sourcesToFieldOptions(sources, context);
});

const populationName = defineModel<string | undefined>('populationName');
const populationDescription = defineModel<string | undefined>(
  'populationDescription',
);
const selectedFeedId = defineModel<number | null | 'add-new-source'>(
  'selectedFeedId',
);
const selectedObject = defineModel<number | null>('selectedObject');
const selectedPopType = defineModel<
  StandardPopulationType | OtherPopulationType | null
>('selectedPopType');
const advancedFilterGroups = defineModel<FilterGroupOutput[]>(
  'advancedFilterGroups',
  { default: () => [] },
);

const route = useRoute();
const initialTab =
  route?.query?.tab === 'filters' ? 1 : props.mode === 'create' ? 0 : 1;
const activeSegmentIndex = ref(initialTab);

const isViewMode = computed(() => props.mode === 'view');

const formConfig = computed<PopulationFormConfig>(() => ({
  isCustomPopulation: props.isCustomPopulation,
  isEdit: props.mode === 'edit',
  isOfflinePartner: props.isOfflinePartner,
  isOpenData: props.isOpenData,
  isSourceLocked: props.isSourceLocked,
  isViewMode: isViewMode.value,
}));

const isDetailsFormValid = computed(() => {
  const hasDataSource =
    !!selectedFeedId.value && selectedFeedId.value !== 'add-new-source';
  const hasObject = !!selectedObject.value;

  if (props.isCustomPopulation) {
    const hasName = !!populationName.value?.trim();
    const hasPopType = !!selectedPopType.value;
    return hasDataSource && hasObject && hasName && hasPopType;
  }

  return hasDataSource && hasObject;
});

const tabs = computed(() => [
  {
    title: 'Details',
    id: 0,
  },
  {
    title: 'Filters',
    id: 1,
    disabled: props.mode === 'create' && !isDetailsFormValid.value,
  },
]);

const showFooterButton = computed(() => {
  if (activeSegmentIndex.value === 0) {
    return props.mode === 'create' && !props.isSourceLocked;
  }
  if (activeSegmentIndex.value === 1) {
    return props.mode !== 'view';
  }
  return false;
});

const isApplyDisabled = computed(() => {
  const groups = advancedFilterGroups.value ?? [];
  if (groups.length === 0) return true;

  const allFiltersComplete = groups.every((group) => {
    const filters = group?.filters ?? [];
    if (filters.length === 0) return true;
    return filters.every((filter) => filter && isFilterComplete(filter));
  });
  return !allFiltersComplete;
});

function handleChangeSegment(id: number) {
  if (id === 1) {
    emit('continue');
  }
  activeSegmentIndex.value = id;
}

function handleFeedChange(feedId: number | 'add-new-source') {
  emit('feed-change', feedId);
}

defineExpose({
  isDetailsFormValid,
});
</script>

<style lang="pcss" scoped>
@reference "../../../css/theme.css";
.c-population-detail-tabs {
  @apply flex flex-col p-16 pb-0 h-full box-border relative;
}

.c-population-detail-tabs__buttons-container {
  @apply sticky bottom-0 z-10 w-full ml-auto mt-auto pointer-events-none p-16 pr-0 flex justify-end;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 100%);

  :deep(.c-bitts-btn) {
    @apply pointer-events-auto!;
  }
}
</style>
